// src/components/DepositModal.js
import { useState } from "react";
import axios from "axios";
import { X, QrCode, Copy, Check, WalletCards, Zap, ShieldCheck } from "lucide-react";
import { Button } from "./ui/button";

// Mesma base do TradingDashboard: https://seu-site/api/trader
const ROOT_API = `${window.location.origin}/api`;
const API = `${ROOT_API}/trader`;

function extractCopyPaste(data) {
  const pix = data?.pix || data?.payment || data?.qr || data || {};

  let payload =
    pix?.copy_paste ||
    pix?.copyPaste ||
    pix?.payload ||
    pix?.qrCode ||
    pix?.emv ||
    data?.copy_paste ||
    data?.copyPaste ||
    data?.payload ||
    data?.qrCode ||
    data?.emv ||
    null;

  // Se vier objeto
  if (payload && typeof payload === "object") {
    payload =
      payload?.payload ||
      payload?.copy_paste ||
      payload?.copyPaste ||
      payload?.qrCode ||
      payload?.emv ||
      null;
  }

  if (payload === null || payload === undefined) return "";
  return String(payload).trim();
}

function extractQrImage(data) {
  const pix = data?.pix || data?.payment || data?.qr || data || {};

  let img =
    pix?.qr_code_image ||
    pix?.qrCodeImage ||
    pix?.encodedImage ||
    pix?.image ||
    data?.qr_code_image ||
    data?.qrCodeImage ||
    data?.encodedImage ||
    data?.image ||
    null;

  if (!img) return null;

  img = String(img).trim();
  if (!img) return null;

  // Se já vier pronto (data URI), mantém
  if (img.startsWith("data:image")) return img;

  // Se vier base64 puro, converte
  return `data:image/png;base64,${img}`;
}

export default function DepositModal({ open, onClose, onSuccess }) {
  const [amount, setAmount] = useState(""); // valor armazenado em centavos
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [pixData, setPixData] = useState(null);
  const [copied, setCopied] = useState(false);

  if (!open) return null;

  const handleCreatePix = async () => {
    setError("");
    setCopied(false);
    setPixData(null);

    const value = Number(amount || "0") / 100;
    if (!Number.isFinite(value) || value <= 0) {
      setError("Informe um valor válido para depósito.");
      return;
    }

    setLoading(true);
    try {
      const resp = await axios.post(`${API}/payments/pix`, {
        amount: value,
      });

      const data = resp?.data || {};
      console.log("[DepositModal] /payments/pix resp:", data);

      if (data.ok === false) {
        setError(
          data.detail ||
            data.error ||
            "Não foi possível gerar o PIX. Tente novamente em instantes."
        );
        return;
      }

      const pix = data?.pix || data?.payment || data?.qr || data || {};
      const copyPayload = extractCopyPaste(data);
      const finalQrImg = extractQrImage(data);

      // ✅ Só falha se não vier NEM QR NEM código
      if (!copyPayload && !finalQrImg) {
        setError(
          "PIX foi criado no servidor, mas a resposta não trouxe QR Code nem código copia-e-cola."
        );
        console.error("[DepositModal] resposta sem QR/payload:", data);
        return;
      }

      setPixData({
        mode: data?.mode || pix?.mode || "unknown",
        qrCodeImage: finalQrImg,
        copyPaste: copyPayload || "",
        deposit: data?.deposit || pix?.deposit || null,
        raw: data, // útil para debug se precisar
      });

      // 🔴 Mantido: não fecha automaticamente
      // if (typeof onSuccess === "function") {
      //   onSuccess();
      // }
    } catch (err) {
      console.error("[DepositModal] erro ao criar PIX:", err);
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.error ||
        "Não foi possível gerar o PIX. Tente novamente em instantes.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!pixData?.copyPaste) return;

    const text = pixData.copyPaste;

    try {
      // 1) Tenta usar Clipboard API (funciona em HTTPS/localhost)
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        // 2) Fallback para ambientes HTTP (192.168.x.x, etc.)
        const textarea = document.createElement("textarea");
        textarea.value = text;
        textarea.style.position = "fixed";
        textarea.style.left = "-9999px";
        textarea.style.top = "-9999px";
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(textarea);

        if (!ok) {
          throw new Error("execCommand copy failed");
        }
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      console.error("Erro ao copiar PIX:", e);
      setCopied(false);
      setError(
        "Não foi possível copiar automaticamente. Selecione o código e copie manualmente."
      );
    }
  };

  const handleClose = () => {
    setAmount("");
    setError("");
    setPixData(null);
    setCopied(false);
    if (typeof onClose === "function") onClose();
  };


  const amountValue = Number(amount || "0") / 100;
  const amountFormatted = amountValue.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });

  const setQuickAmount = (value) => {
    setAmount(String(Math.round(Number(value) * 100)));
    setError("");
    setPixData(null);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/75 backdrop-blur-sm sm:items-center sm:p-4">
      <div className="flex h-[96dvh] w-full flex-col overflow-hidden rounded-t-[28px] border border-slate-700/80 bg-slate-950 shadow-2xl sm:h-auto sm:max-h-[94vh] sm:max-w-lg sm:rounded-[28px]">
        <div className="flex items-center justify-between border-b border-slate-800 px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-500/15">
              <WalletCards className="h-6 w-6 text-emerald-400" />
            </div>
            <div>
              <h2 className="text-lg font-black text-white">Depositar via PIX</h2>
              <p className="text-xs text-slate-400">Rápido e liberado após a confirmação</p>
            </div>
          </div>
          <button type="button" onClick={handleClose}
            className="flex h-11 items-center gap-2 rounded-xl border border-slate-700 bg-slate-900 px-3 text-sm font-bold text-slate-200">
            <X className="h-5 w-5" /> Fechar
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-5 sm:px-5">
          {!pixData ? (
            <div className="space-y-5">
              <div>
                <p className="mb-2 text-sm font-bold text-slate-200">Valor do depósito</p>
                <div className="rounded-3xl border border-slate-700 bg-slate-900/80 p-4 focus-within:border-emerald-500/70">
                  <p className="mb-1 text-xs font-bold uppercase tracking-widest text-slate-500">Digite o valor</p>
                  <div className="flex items-center">
                    <span className="mr-2 text-2xl font-black text-emerald-400">R$</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={amount ? amountValue.toLocaleString("pt-BR", {minimumFractionDigits:2, maximumFractionDigits:2}) : ""}
                      onChange={(e) => {
                        setAmount(e.target.value.replace(/\D/g, "").slice(0, 12));
                        setError("");
                        setPixData(null);
                      }}
                      placeholder="0,00"
                      className="min-w-0 flex-1 bg-transparent text-4xl font-black text-white outline-none placeholder:text-slate-700 sm:text-5xl"
                    />
                  </div>
                </div>
              </div>

              <div>
                <p className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-500">Valores rápidos</p>
                <div className="grid grid-cols-3 gap-2">
                  {[100, 500, 1000, 2000, 5000, 10000].map((value) => (
                    <button key={value} type="button" onClick={() => setQuickAmount(value)}
                      className={`rounded-2xl border px-2 py-3 text-sm font-black transition active:scale-95 ${
                        Math.abs(amountValue-value) < 0.001
                          ? "border-emerald-400 bg-emerald-500/15 text-emerald-300"
                          : "border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800"
                      }`}>
                      {value.toLocaleString("pt-BR",{style:"currency",currency:"BRL",maximumFractionDigits:0})}
                    </button>
                  ))}
                </div>
              </div>

              <div className="rounded-2xl border border-slate-800 bg-slate-900/55 p-4">
                <div className="flex gap-3">
                  <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-emerald-400" />
                  <div>
                    <p className="text-sm font-bold text-white">Pagamento seguro via PIX</p>
                    <p className="mt-1 text-xs leading-relaxed text-slate-400">
                      Gere o PIX e pague pelo aplicativo do seu banco. Após a confirmação, o valor será liberado no seu saldo de negociação.
                    </p>
                  </div>
                </div>
              </div>

              {error && <div className="rounded-2xl border border-red-500/30 bg-red-500/10 p-3 text-sm font-semibold text-red-300 whitespace-pre-line">{error}</div>}

              <Button type="button" onClick={handleCreatePix} disabled={loading || amountValue <= 0}
                className="h-16 w-full rounded-2xl bg-gradient-to-r from-emerald-500 to-cyan-500 text-base font-black text-white shadow-xl shadow-emerald-500/15 disabled:opacity-50">
                <span className="flex items-center justify-center gap-2">
                  <Zap className={`h-5 w-5 ${loading ? "animate-pulse" : ""}`} />
                  {loading ? "GERANDO PIX..." : amountValue > 0 ? `GERAR PIX • ${amountFormatted}` : "GERAR PIX"}
                </span>
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="rounded-3xl border border-emerald-500/25 bg-emerald-500/10 p-4 text-center">
                <QrCode className="mx-auto mb-2 h-8 w-8 text-emerald-400" />
                <p className="text-xs font-bold uppercase tracking-widest text-emerald-300">PIX pronto para pagamento</p>
                <p className="mt-1 text-3xl font-black text-white">{amountFormatted}</p>
                {pixData.deposit?.id && <p className="mt-1 text-xs text-slate-500">Depósito #{pixData.deposit.id}</p>}
              </div>

              {pixData.qrCodeImage && (
                <div className="rounded-3xl border border-slate-800 bg-white p-4">
                  <img src={pixData.qrCodeImage} alt="QR Code PIX" className="mx-auto h-60 w-60 max-w-full rounded-xl object-contain" />
                  <p className="mt-3 text-center text-xs font-semibold text-slate-600">Escaneie com o aplicativo do seu banco</p>
                </div>
              )}

              <div className="rounded-3xl border border-slate-800 bg-slate-900/70 p-4">
                <p className="mb-2 text-sm font-bold text-white">PIX Copia e Cola</p>
                {pixData.copyPaste ? (
                  <>
                    <div className="max-h-24 overflow-y-auto break-all rounded-2xl border border-slate-700 bg-slate-950 p-3 text-xs text-slate-300">{pixData.copyPaste}</div>
                    <Button type="button" onClick={handleCopy}
                      className={`mt-3 h-14 w-full rounded-2xl text-sm font-black ${copied ? "bg-emerald-500" : "bg-slate-800 hover:bg-slate-700"}`}>
                      <span className="flex items-center justify-center gap-2">
                        {copied ? <Check className="h-5 w-5" /> : <Copy className="h-5 w-5" />}
                        {copied ? "CÓDIGO COPIADO!" : "COPIAR CÓDIGO PIX"}
                      </span>
                    </Button>
                  </>
                ) : (
                  <div className="rounded-2xl border border-amber-500/25 bg-amber-500/10 p-3 text-sm text-amber-200">
                    Este PIX foi gerado apenas com QR Code. Escaneie o código no aplicativo do seu banco.
                  </div>
                )}
              </div>

              <p className="text-center text-xs leading-relaxed text-slate-400">
                Assim que o pagamento for confirmado, o valor será liberado no seu saldo.
              </p>

              {error && <div className="rounded-2xl border border-red-500/30 bg-red-500/10 p-3 text-sm font-semibold text-red-300 whitespace-pre-line">{error}</div>}

              {typeof onSuccess === "function" && (
                <Button type="button" onClick={() => onSuccess()} variant="outline"
                  className="h-14 w-full rounded-2xl border-slate-700 bg-slate-900 text-sm font-black text-slate-100 hover:bg-slate-800">
                  ATUALIZAR MEU SALDO
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
